// One A4 page for every document (docs/plans/doc-pages.md; boards DocQuotePrint, DocPOPrint):
// bills, quotations, sales orders and purchase orders all become an `A4Doc`, which the HTML
// print (`A4Page` in print.tsx) and the PDF (a4-pdf.ts) both draw, so paper and file match for
// every document. Pure; amounts come out formatted.
import { amountInWords, formatMoney, formatQty, purchaseLineCost } from '@ims/core'
import type {
  PurchaseOrderRow,
  SalesOrderLineRow,
  SalesOrderRow,
  TenantSettingsRow,
} from '@ims/data'
import {
  dualDay,
  printedLine,
  type BillLine,
  type BillView,
  type PrintSettingsLike,
} from './bill-view.ts'
import { registrationOf, showsVat, taxNumberLabel } from './registration.ts'

export type DocColumnKey =
  'item' | 'hs' | 'sku' | 'unit' | 'qty' | 'rate' | 'disc' | 'tax' | 'amount'

export interface DocColumn {
  key: DocColumnKey
  label: string
  align: 'left' | 'right'
}

export interface DocRow {
  no: number
  cells: Partial<Record<DocColumnKey, string>>
  /** Grey italic lines under the item (batch, serials). */
  notes: string[]
}

export interface A4Doc {
  /** "Tax invoice", "Quotation", "Purchase order – CANCELLED". */
  title: string
  number: string
  copyLabel: string | null
  seller: BillView['seller']
  /** "Bill to:" / "Quotation for:" / "Order for:" / "Supplier:" and the lines under it. */
  party: { heading: string; lines: string[] }
  detailsHeading: string
  details: BillLine[]
  columns: DocColumn[]
  rows: DocRow[]
  totals: BillLine[]
  credits: BillLine[]
  after: BillLine[]
  words: string
  /** Remarks, terms, a note for the supplier: each a heading and its text. */
  notes: { heading: string; text: string }[]
  /** The bill's QR (`billQrText`); none on paperwork. */
  qrText: string | null
  /** Signature lines, left to right; "For: <business>" is always the last. */
  signatures: string[]
  /** A grey line under the signatures ("This is a quotation, not an invoice."). */
  notice: string | null
  footer: string | null
  contact: string | null
  printed: string | null
}

type DocSettings =
  | (NonNullable<PrintSettingsLike> & Partial<Pick<TenantSettingsRow, 'quote_terms'>>)
  | null
  | undefined

const ITEM: DocColumn = { key: 'item', label: 'Item / description', align: 'left' }
const col = (key: DocColumnKey, label: string): DocColumn => ({ key, label, align: 'right' })

/** The bill's own model (lib/bill-view.ts) as an A4 page: the words a bill has always printed. */
export function billA4(view: BillView): A4Doc {
  // Kept even when no line has a code: most bills in Nepal carry the column.
  const columns: DocColumn[] = [
    { key: 'hs', label: 'HS code', align: 'left' },
    ITEM,
    col('qty', 'Qty'),
    col('rate', 'Rate'),
  ]
  if (view.discounts) columns.push(col('disc', 'Disc.'))
  if (view.vat) columns.push(col('tax', 'Tax'))
  columns.push(col('amount', 'Amount'))
  const partyLines = [
    view.buyer.name,
    view.buyer.address,
    view.buyer.pan && `PAN: ${view.buyer.pan}`,
    view.service &&
      [
        view.service.heading,
        view.service.right,
        view.service.extra,
        view.service.servedBy && `Served by ${view.service.servedBy}`,
      ]
        .filter(Boolean)
        .join(' · '),
  ]
  return {
    title: view.title,
    number: view.number,
    copyLabel: view.copyLabel,
    seller: view.seller,
    party: { heading: 'Bill to:', lines: partyLines.filter((l): l is string => Boolean(l)) },
    detailsHeading: 'Invoice details:',
    details: view.details,
    columns,
    rows: view.rows.map((r) => ({
      no: r.no,
      cells: {
        item: r.description,
        hs: r.hsCode,
        qty: r.qty,
        rate: r.rate,
        disc: r.discount,
        tax: r.tax,
        amount: r.amount,
      },
      notes: r.notes,
    })),
    totals: view.totals,
    credits: view.credits,
    after: view.after,
    words: view.words,
    notes: view.remarks ? [{ heading: 'Remarks:', text: view.remarks }] : [],
    qrText: view.qrText,
    signatures: [`For: ${view.seller.name}`],
    notice: null,
    footer: view.footer,
    contact: view.contact,
    printed: view.printed,
  }
}

function sellerOf(settings: DocSettings): BillView['seller'] {
  const registration = registrationOf(settings)
  return {
    name: settings?.business_name ?? '',
    legalName: settings?.legal_name ?? null,
    address: settings?.address ?? null,
    taxLine: settings?.pan_vat_number
      ? `${taxNumberLabel(registration)}: ${settings.pan_vat_number}`
      : null,
    phone: settings?.phone ?? null,
    email: settings?.email ?? null,
    logo: settings?.logo ?? null,
    logoOnReceipt: settings?.logo_on_receipt ?? true,
  }
}

function contactOf(settings: DocSettings, about: string): string | null {
  const bits = [
    settings?.phone && `call ${settings.phone}`,
    settings?.email && `write to ${settings.email}`,
  ].filter(Boolean)
  return bits.length ? `For any questions about this ${about}, ${bits.join(' or ')}.` : null
}

function numberOf(doc: { number: string | null; status: string }): string {
  return doc.number ?? (doc.status === 'draft' ? 'Draft' : 'Number assigned on sync')
}

function titleOf(name: string, status: string): string {
  if (status === 'draft') return `${name} (draft)`
  return status === 'cancelled' ? `${name} – CANCELLED` : name
}

export interface PrintedBy {
  printedBy?: string | null
  /** An ISO timestamp. */
  printedAt?: string
}

export interface SalesDocInput extends PrintedBy {
  order: SalesOrderRow
  lines: readonly SalesOrderLineRow[]
  settings: DocSettings
  /** The document it revises or was made from: its number. */
  reference?: { label: string; number: string } | null
  /** The customer's phone, from the customer record (the document keeps no copy). */
  phone?: string | null
}

/** A quotation or a sales order on A4: the bill's layout, saying it is not an invoice. */
export function salesDocView(input: SalesDocInput): A4Doc {
  const { order, settings } = input
  const plain = (p: number) => formatMoney(p)
  const quotation = order.kind === 'quotation'
  const name = quotation ? 'Quotation' : 'Sales order'
  const registration = registrationOf(settings)
  const vat = showsVat(registration, order.tax_paisa)
  const discounts = input.lines.some((l) => l.discount_paisa > 0)

  const details: BillLine[] = [
    { label: quotation ? 'Quotation no.' : 'Order no.', value: numberOf(order) },
    { label: 'Date', value: dualDay(order.doc_date) },
  ]
  if (order.valid_until) details.push({ label: 'Valid until', value: dualDay(order.valid_until) })
  if (order.expected_date) details.push({ label: 'Expected', value: dualDay(order.expected_date) })
  if (input.reference) details.push({ label: input.reference.label, value: input.reference.number })

  const columns: DocColumn[] = [ITEM, col('qty', 'Qty'), col('rate', 'Rate')]
  if (discounts) columns.push(col('disc', 'Disc.'))
  if (vat) columns.push(col('tax', 'Tax'))
  columns.push(col('amount', 'Amount'))

  const totals: BillLine[] = [{ label: 'Sub total', value: plain(order.subtotal_paisa) }]
  if (order.discount_paisa > 0)
    totals.push({ label: 'Discount', value: `−${plain(order.discount_paisa)}` })
  if (vat) {
    totals.push({ label: 'Taxable amount', value: plain(order.taxable_paisa) })
    totals.push({ label: 'VAT', value: plain(order.tax_paisa) })
  }
  if (order.round_off_paisa !== 0)
    totals.push({ label: 'Round off', value: plain(order.round_off_paisa) })
  totals.push({
    label: 'Net total',
    value: formatMoney(order.total_paisa, { symbol: settings?.currency_symbol ?? 'Rs.' }),
    strong: true,
  })

  const terms = order.terms ?? (quotation ? settings?.quote_terms : null)
  const notes: A4Doc['notes'] = []
  if (order.notes) notes.push({ heading: 'Remarks:', text: order.notes })
  if (terms) notes.push({ heading: 'Terms', text: terms })
  const invoice = registration === 'vat' ? 'a tax invoice' : 'an invoice'

  return {
    title: titleOf(name, order.status),
    number: numberOf(order),
    copyLabel: null,
    seller: sellerOf(settings),
    party: {
      heading: quotation ? 'Quotation for:' : 'Order for:',
      lines: [
        order.customer_name || 'Customer',
        order.customer_address,
        input.phone && `Phone: ${input.phone}`,
        order.customer_pan && `PAN: ${order.customer_pan}`,
      ].filter((l): l is string => Boolean(l)),
    },
    detailsHeading: `${name} details:`,
    details,
    columns,
    rows: input.lines.map((l) => ({
      no: l.line_no,
      cells: {
        item: l.description,
        qty: formatQty(l.qty_milli),
        rate: plain(l.unit_price_paisa),
        disc: l.discount_paisa ? plain(l.discount_paisa) : '',
        tax: vat ? (l.tax_rate_bp ? `${l.tax_rate_bp / 100}% VAT` : 'Exempt') : '',
        amount: plain(l.total_paisa),
      },
      notes: [],
    })),
    totals,
    credits: [],
    after: [],
    words: amountInWords(order.total_paisa),
    notes,
    qrText: null,
    signatures: [
      quotation ? 'Accepted by the customer' : 'Received by',
      `For: ${settings?.business_name ?? ''}`,
    ],
    notice: quotation
      ? `This is a quotation, not ${invoice}. Prices hold until the date above.`
      : `This is an order, not ${invoice}.`,
    footer: null,
    contact: contactOf(settings, quotation ? 'quotation' : 'order'),
    printed: printedLine(input.printedBy, input.printedAt),
  }
}

/** A line of a purchase order as printed: the order stores no description, so the name comes with it. */
export interface PurchaseDocLine {
  id: string
  line_no: number
  name: string
  sku: string | null
  unit: string
  qty_ordered_milli: number
  unit_cost_paisa: number
}

export interface PurchaseDocInput extends PrintedBy {
  order: PurchaseOrderRow
  lines: readonly PurchaseDocLine[]
  supplier: {
    name: string
    address: string | null
    phone: string | null
    pan_vat_number: string | null
  } | null
  /** Where the goods are expected, when the business has more than one place. */
  deliverTo: string | null
  settings: DocSettings
  reference?: { label: string; number: string } | null
}

/** A purchase order on A4, to hand or send to the supplier. */
export function purchaseDocView(input: PurchaseDocInput): A4Doc {
  const { order, settings, supplier } = input
  const plain = (p: number) => formatMoney(p)
  const details: BillLine[] = [
    { label: 'Order no.', value: numberOf(order) },
    { label: 'Date', value: dualDay(order.order_date) },
  ]
  if (order.expected_date) details.push({ label: 'Needed by', value: dualDay(order.expected_date) })
  if (input.deliverTo) details.push({ label: 'Deliver to', value: input.deliverTo })
  if (input.reference) details.push({ label: input.reference.label, value: input.reference.number })
  return {
    title: titleOf('Purchase order', order.status),
    number: numberOf(order),
    copyLabel: null,
    seller: sellerOf(settings),
    party: {
      heading: 'Supplier:',
      lines: [
        supplier?.name ?? 'Supplier',
        supplier?.address,
        supplier?.phone && `Phone: ${supplier.phone}`,
        supplier?.pan_vat_number && `PAN: ${supplier.pan_vat_number}`,
      ].filter((l): l is string => Boolean(l)),
    },
    detailsHeading: 'Purchase order details:',
    details,
    columns: [
      { key: 'item', label: 'Item', align: 'left' },
      { key: 'sku', label: 'SKU', align: 'left' },
      { key: 'unit', label: 'Unit', align: 'left' },
      col('qty', 'Qty'),
      col('rate', 'Cost'),
      col('amount', 'Amount'),
    ],
    rows: input.lines.map((l) => ({
      no: l.line_no,
      cells: {
        item: l.name,
        sku: l.sku ?? '',
        unit: l.unit,
        qty: formatQty(l.qty_ordered_milli),
        rate: plain(l.unit_cost_paisa),
        amount: plain(
          purchaseLineCost({ qtyMilli: l.qty_ordered_milli, unitCostPaisa: l.unit_cost_paisa }),
        ),
      },
      notes: [],
    })),
    totals: [
      {
        label: 'Net total',
        value: formatMoney(order.total_paisa, { symbol: settings?.currency_symbol ?? 'Rs.' }),
        strong: true,
      },
    ],
    credits: [],
    after: [],
    words: amountInWords(order.total_paisa),
    notes: order.notes ? [{ heading: 'Note for the supplier', text: order.notes }] : [],
    qrText: null,
    signatures: ['Received by', `For: ${settings?.business_name ?? ''}`],
    notice: 'Please quote this order number on your bill and delivery note.',
    footer: null,
    contact: contactOf(settings, 'order'),
    printed: printedLine(input.printedBy, input.printedAt),
  }
}
